import { container } from "tsyringe";

import type { IConfigurationDTO } from "coreframetypings/dto";
import type Principal from "@/Model/Principal";
import type UIInterceptor from "@/UIInterceptors/UIInterceptor";
import type DetailViewContext from "@/ViewModels/Contexts/DetailViewContext";
import type MessageBoxViewModel from "@/ViewModels/Controls/MessageBoxViewModel";
import type ViewModel from "@/ViewModels/ViewModel";
import type { IDropdownMenuItem } from "#/CoreFrameInterfaces";
import type User from "@/Model/User";

export interface IRADApplication {
    appName: KnockoutObservable<string>;
    description: KnockoutObservable<string>;
    ergName: KnockoutObservable<string>;
    buildVersion: KnockoutObservable<string>;
    rootLayoutVM: ViewModel;
    isSystemLocked: KnockoutObservable<boolean>;
    lastCacheUpdate: KnockoutObservable<any>;
    lastRightCacheUpdate: KnockoutObservable<any>;
    principalTypeName: KnockoutObservable<string>;
    systemLockMessage: KnockoutObservable<string>;
    isForceLogout: KnockoutObservable<boolean>;
    isChangePrincipalEnabled: KnockoutObservable<boolean>;
    templateStyle: KnockoutComputed<string>;
    iconUrl: KnockoutComputed<string>;
    tooltip: KnockoutObservable<string>;
    configuration: IConfigurationDTO;
    menuSprite: any;
    menuSpriteLoaded: JQueryDeferred<boolean>;
    user: User;
    principal: Principal;
    deployedModules: KnockoutObservable<any>;
    pdApplicationChangedTime: KnockoutObservable<any>;
    licensedModules: KnockoutObservable<any>;
    isRADlicensed: KnockoutComputed<boolean>;
    expiredModules: KnockoutObservable<any>;
    modules: KnockoutObservable<any>;
    currentListViewContext: KnockoutObservable<any>;
    currentSummaryViewContext: KnockoutObservable<any>;
    currentDetailViewContext: KnockoutObservable<DetailViewContext>;
    defaultPersistenceContextId: KnockoutObservable<any>;
    uiInterceptors: Array<UIInterceptor>;
    isInitialised: boolean;
    serverUrl: string;
    radEnvironmentServiceUrl: string;
    radEnvironmentTemplateHandler: string;
    radEnvironmentDataHandler: string;
    radEnvironmentMediumHandler: string;
    radEnvironmentViewExtensionHandler: string;
    token: string;
    xcsrfToken: string;
    ssoUserName: string;
    profiler: any;
    BundlingEnabled: boolean;
    IsReducedView: boolean;

    /**
     * URL für den View-Extension-Serivces des RAD-Environments
     * Verweist intern auf den ResourceManager welcher die aktuelle Sprache verwaltet
     */
    currentLanguage(): any;

    /**
     * Beinhalte alle übersetzten standard ResourceItems für eine RAD-Anwendung
     * Verweist intern auf den ResourceManager welcher die Resource-Elemente verwaltet
     */
    resourceItemValues(): any;

    /**
     * Öffnet eine MessageBox
     */
    isModalWindowOpen(): boolean;

    increaseModalWindowOpenCounter(): void;

    decreaseModalWindowOpenCounter(): void;

    showMessageBox(messageBoxViewModel: MessageBoxViewModel, isNavigatable?: boolean): void;

    /**
     * Behandelt eine Datenbank-Exception
     */
    handleDatabaseException(): void;

    /**
     * Wird aufgerufen, nachdem die Anwendung gestartet wurde.
     * Hinweis: Die Function 'callback' muss zwingend aufgerufen werden.
     */
    onAfterStartup(callback: () => void): void;

    /**
     * Wird aufgerufen, nachdem ein Benutzer angemeldet wurde.
     * Hinweis: Die Function 'callback' muss zwingend aufgerufen werden.
     */
    onAfterLogin(callback: () => void): void;

    /**
     * Wird aufgerufen, bevor das Rootlayout geladen oder aktualisiert wird.
     */
    onBeforeLoadRootLayout(): void;

    /**
     * Wird aufgerufen, sobald das Rootlayout geladen wurde.
     */
    onAfterLoadRootLayout(): void;

    /**
     * Wird aufgerufen bevor der Menübaum geladen wird.
     */
    onBeforeLoadMenuTree(rootNode): void;

    /**
     * Wird aufgerufen bevor das UserDropdownMenu geladen wird.
     */
    onBeforeLoadUserDropdownMenu(menuItems: IDropdownMenuItem[]): void;

    /**
     * Wird aufgerufen bevor der Navigationsbaum geladen wird.
     */
    onBeforeLoadNavigationTree(rootNode): void;

    /**
     * Methode, welche nach einem Login die Login-Informationen setzt
     */
    afterLogin(loginDTO, principalDTO): void;

    beforeLogout(): void;

    /**
     * Methode, welche nach dem Logout zum Login navigiert
     */
    afterLogout(): void;

    /**
     * Gibt an, ob eine genannte Systemvariable gesetzt und auf True gestellt ist
     */
    isSystemParamSetAndTrue(name: string): boolean;

    /**
     * Liefert den Wert einer Systemvariablen
     */
    getSystemParam(name: string, parsingFunc?: (param: any) => any): any;

    /**
     * Registrert Keyboard-Events
     */
    manageKeyboard(): void;

    /**
     * Initialisiert den Profiler
     */
    toggleProfiler(): void;

    /**
     * Führt ein Cache-Update durch
     */
    updateCache(): JQueryPromise<void>;

    /**
     * Führt ein Update der ResouceItems durch
     */
    updateAllResourceItems(): JQueryPromise<void>;

    unauthorizedRequestCallback(): void;

    noConnectionToServerCallback(): void;

    /**
     * Instanziiert alle geladenen UIInterceptoren
     */
    loadUIInterceptors(): void;

    /**
     * Initialisiert die Anwendung
     */
    init(): void;

    /**
     * Liefert zu einem Mandanten-Filter-Modus den entsprechend Client-Wert.
     */
    getServerValueForPrincipalFilterMode(keyOrValue: string): string;

    /**
     * Liefert zu einem Mandanten-Filter-Modus den entsprechend Server-Wert.
     */
    getClientValueForPrincipalFilterMode(keyOrValue: string): string;
}

export default class RADApplication {
    public static getInstance(): IRADApplication {
        return container.resolve<IRADApplication>("RADApplication");
    }
}